import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import "bootstrap-icons/font/bootstrap-icons.css";

/** UI create options — `id` maps to backend asset types where possible (`poster` → `image`). */
export const CREATE_CONTENT_TYPES = [
    {
        id: "post",
        assetType: "post",
        label: "Post",
        description: "A feed update with caption and optional media.",
        icon: "bi-card-text",
    },
    {
        id: "carousel",
        assetType: "carousel",
        label: "Carousel",
        description: "Multi-slide swipeable post for your feed.",
        icon: "bi-collection",
    },
    {
        id: "story",
        assetType: "story",
        label: "Story",
        description: "Full-screen vertical story that lasts 24 hours.",
        icon: "bi-clock-history",
    },
    {
        id: "poster",
        assetType: "image",
        label: "Poster",
        description: "One bold visual designed to stop the scroll.",
        icon: "bi-image",
    },
];

export default function CreateContentTypeModal({ open = false, onClose, onContinue }) {
    const [selectedId, setSelectedId] = useState(null);

    useEffect(() => {
        if (!open) {
            setSelectedId(null);
            return undefined;
        }

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        const handleKeyDown = (event) => {
            if (event.key === "Escape") onClose?.();
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => {
            document.body.style.overflow = previousOverflow;
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [open, onClose]);

    if (!open) return null;

    const selected = CREATE_CONTENT_TYPES.find((item) => item.id === selectedId) ?? null;

    const handleContinue = () => {
        if (!selected) return;
        onContinue?.(selected);
    };

    return createPortal(
        <div
            className="fixed inset-0 z-[200] flex items-end justify-center bg-slate-900/50 p-0 sm:items-center sm:p-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="create-content-type-title"
            onClick={onClose}
        >
            <div
                className="flex w-full max-w-md flex-col overflow-hidden rounded-t-2xl bg-white shadow-xl sm:rounded-2xl"
                onClick={(event) => event.stopPropagation()}
            >
                <div className="flex shrink-0 items-center justify-between border-b border-slate-200 px-4 py-4 sm:px-5">
                    <div>
                        <h2
                            id="create-content-type-title"
                            className="mb-0 text-lg font-semibold text-slate-900"
                        >
                            Create
                        </h2>
                        <p className="mb-0 mt-1 text-sm text-slate-500">
                            What do you want to make?
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-100 hover:text-slate-900"
                        aria-label="Close"
                    >
                        <i className="bi bi-x-lg" aria-hidden />
                    </button>
                </div>

                <div
                    className="grid grid-cols-2 gap-3 px-4 py-4 sm:px-5 sm:py-5"
                    role="radiogroup"
                    aria-label="Content type"
                >
                    {CREATE_CONTENT_TYPES.map((item) => {
                        const checked = selectedId === item.id;

                        return (
                            <button
                                key={item.id}
                                type="button"
                                role="radio"
                                aria-checked={checked}
                                onClick={() => setSelectedId(item.id)}
                                className={`flex flex-col items-start gap-2 rounded-xl border p-3.5 text-left transition sm:p-4 ${
                                    checked
                                        ? "border-slate-900 bg-slate-50 shadow-sm"
                                        : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50/80"
                                }`}
                            >
                                <span
                                    className={`inline-flex h-10 w-10 items-center justify-center rounded-xl ${
                                        checked
                                            ? "bg-slate-900 text-white"
                                            : "bg-slate-100 text-slate-700"
                                    }`}
                                >
                                    <i className={`bi ${item.icon} text-lg`} aria-hidden />
                                </span>
                                <span className="text-sm font-semibold text-slate-900">
                                    {item.label}
                                </span>
                                <span className="text-xs leading-snug text-slate-500">
                                    {item.description}
                                </span>
                            </button>
                        );
                    })}
                </div>

                <div className="shrink-0 border-t border-slate-200 px-4 py-3 sm:px-5">
                    <button
                        type="button"
                        onClick={handleContinue}
                        disabled={!selected}
                        className="btn-primary-app inline-flex w-full items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        <span>Select a trend</span>
                        <span aria-hidden>&rarr;</span>
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
}
